import React, { useCallback, useMemo } from 'react';
import styled from 'types/theme.types';
import TextInput from './input-components/text-input';
import type { InputProps } from 'antd/lib/input';
import { SearchOutlined } from '@ant-design/icons';
import Tooltip from 'components/common/tooltip';
import debounce from 'lodash/debounce';
import { isTestNodeEnv } from 'constants/config';
import type { InputRef } from 'antd';
import { useIntl } from 'react-intl';
import Markdown from 'components/common/markdown';

export interface SearchBarProps extends InputProps {
  placeholder?: string;
  onFilter: (query: string) => void;
  debounceMs?: number;
  hideError?: boolean;
  error?: string | null;
  showExtendedSearchSyntax?: boolean;
}

export const DEFAULT_DEBOUNCE = isTestNodeEnv ? 5 : 250;

// eslint-disable-next-line react/display-name
const SearchBar: React.FunctionComponent<SearchBarProps> = React.forwardRef<InputRef, SearchBarProps>(
  (
    {
      onFilter,
      placeholder,
      hideError,
      debounceMs = DEFAULT_DEBOUNCE,
      value,
      error,
      showExtendedSearchSyntax = false,
      ...otherProps
    }: SearchBarProps,
    ref
  ) => {
    const onFilterWithDebounce = useMemo(() => {
      return debounce(async searchValue => {
        onFilter(searchValue);
      }, debounceMs || 250);
    }, [debounceMs, onFilter]);

    const setSearch = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterWithDebounce(e.target.value);
      },
      [onFilterWithDebounce]
    );

    const intl = useIntl();
    const tooltipContent = useMemo(() => {
      let content: string = placeholder ? intl.formatMessage({ id: placeholder }) : '';
      if (showExtendedSearchSyntax) {
        if (content) {
          content += '\n\n---\n\n';
        }
        content += intl.formatMessage({ id: 'search-bar.extended-search-syntax.tooltip' });
      }
      return content;
    }, [placeholder, showExtendedSearchSyntax, intl]);

    return (
      <StyledTextInput
        data-e2e="search-bar"
        error={error}
        placeholder={placeholder}
        hideError={!!hideError}
        {...otherProps}
        ref={ref}
        onChange={setSearch}
        suffix={
          <Tooltip title={<StyledMarkdown markdown={tooltipContent} />} placement="right">
            <SearchOutlined />
          </Tooltip>
        }
      />
    );
  }
);

const StyledTextInput = styled(TextInput)`
  border: 1px solid ${({ theme }) => theme.lightGray} !important;
`;

const StyledMarkdown = styled(Markdown)`
  &&& p:last-of-type {
    margin-bottom: 0;
  }
`;

export default SearchBar;
